import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { MdAdd, MdConfirmationNumber, MdInfoOutline } from "react-icons/md";
import { toast } from "react-toastify";
import { getClientTicketSla, listClientTickets } from "./ticketApi";
import MonthSelect from "../../../../components/slaSummary/MonthSelect";
import SlaSummary, { SLA_RULE } from "../../../../components/slaSummary/SlaSummary";
import { mesAtual } from "../../../../components/slaSummary/slaFormat";
import { mensagemDeAreaNegada } from "../portalAreas";
import {
  CLIENT_TICKET_STATE_LABELS,
  CLIENT_TICKET_STATE_TONE,
  ClientTicket,
  ClientTicketListFilter,
  ClientTicketSla,
} from "./ticketTypes";
import OpenTicketModal from "./OpenTicketModal";
import TicketDetailModal from "./TicketDetailModal";
import { mensagemDoServidor } from "../../../../utils/mensagemDoServidor";

const fmtDate = (iso?: string) => (iso ? new Date(iso).toLocaleDateString("pt-BR") : "—");

interface Props {
  /**
   * Verdadeiro quando quem está olhando é um usuário interno pré-visualizando o portal de um
   * cliente. Aqui não é só escrita que fica indisponível: as rotas de `/ClientPortal/tickets`
   * resolvem o cliente pelo vínculo `ClientUserEntity` do usuário AUTENTICADO (D6), e um usuário
   * interno não tem esse vínculo — então a LEITURA também não funciona (o servidor responde 403).
   * Por isso esta aba nem tenta chamar a API em preview; mostra só a explicação abaixo.
   */
  readOnly: boolean;
}

const TicketsTab: React.FC<Props> = ({ readOnly }) => {
  const [filter, setFilter] = useState<ClientTicketListFilter>("open");
  const [tickets, setTickets] = useState<ClientTicket[]>([]);
  const [loading, setLoading] = useState(false);
  const [showOpenModal, setShowOpenModal] = useState(false);
  const [selectedId, setSelectedId] = useState<number | null>(null);

  const [slaMonth, setSlaMonth] = useState<string | null>(mesAtual());
  const [sla, setSla] = useState<ClientTicketSla | null>(null);
  const [slaError, setSlaError] = useState(false);

  // Em preview não chama (mesmo motivo da lista: sem vínculo de cliente, o servidor responde 403).
  useEffect(() => {
    if (readOnly) return;
    let cancelado = false;
    setSlaError(false);
    getClientTicketSla(slaMonth)
      .then((d) => !cancelado && setSla(d))
      .catch(() => !cancelado && setSlaError(true));
    return () => {
      cancelado = true;
    };
  }, [slaMonth, readOnly]);

  const reload = () => {
    if (readOnly) return; // Sem vínculo de cliente em preview — chamar a API só renderia um 403.
    setLoading(true);
    listClientTickets(filter)
      .then(setTickets)
      .catch((e: any) => toast.error(mensagemDeAreaNegada(e) ?? mensagemDoServidor(e, "Erro ao carregar os chamados.")))
      .finally(() => setLoading(false));
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(reload, [filter, readOnly]);

  if (readOnly) {
    return (
      <div className="flex items-start gap-3 rounded-xl border border-dv-blue-100 bg-dv-blue-50 px-4 py-4 dark:border-dv-blue-900/40 dark:bg-dv-blue-900/20">
        <MdInfoOutline className="mt-0.5 h-5 w-5 shrink-0 text-dv-blue-600 dark:text-dv-cyan-300" aria-hidden="true" />
        <p className="text-sm text-dv-blue-700 dark:text-dv-cyan-200">
          A visão de chamados do cliente não é acessível em pré-visualização: o portal resolve o
          cliente pelo login do próprio dono do chamado, e um usuário interno não tem esse vínculo.
          Para ver ou atender os chamados deste cliente, use a{" "}
          <Link to="/admin/tickets" className="font-semibold underline hover:no-underline">
            tela interna de Chamados
          </Link>
          .
        </p>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      <section aria-label="SLA dos chamados" className="rounded-2xl border border-gray-100 p-4 dark:border-white/10">
        <div className="flex flex-wrap items-end justify-between gap-3">
          <h3 className="text-base font-extrabold text-navy-700 dark:text-white">SLA dos seus chamados</h3>
          <MonthSelect id="portal-sla-month" value={slaMonth} onChange={setSlaMonth} />
        </div>
        <p className="mt-2 text-xs text-gray-700 dark:text-gray-300">{SLA_RULE}</p>
        <div className="mt-3">
          {sla ? (
            <SlaSummary data={sla} />
          ) : (
            <p className="py-4 text-center text-sm text-gray-700 dark:text-gray-300">
              {slaError ? "Não foi possível carregar o indicador de SLA agora." : "Carregando…"}
            </p>
          )}
        </div>
      </section>

      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2 rounded-xl bg-gray-50 p-1 dark:bg-navy-900">
          <FilterBtn active={filter === "open"} onClick={() => setFilter("open")}>
            Em aberto
          </FilterBtn>
          <FilterBtn active={filter === "closed"} onClick={() => setFilter("closed")}>
            Encerrados
          </FilterBtn>
        </div>

        <button
          type="button"
          onClick={() => setShowOpenModal(true)}
          className="dv-sheen flex items-center gap-1.5 rounded-xl bg-dv-gradient px-4 py-2.5 text-sm font-bold text-white shadow-dv-glow transition-all active:scale-[.98]"
        >
          <MdAdd className="h-4 w-4" aria-hidden="true" />
          Abrir chamado
        </button>
      </div>

      {loading ? (
        <div className="py-12 text-center text-sm text-gray-700 dark:text-gray-300">Carregando…</div>
      ) : tickets.length === 0 ? (
        <p className="rounded-xl border border-dashed border-gray-200 py-10 text-center text-sm text-gray-700 dark:border-white/10 dark:text-gray-300">
          {filter === "open" ? "Nenhum chamado em aberto." : "Nenhum chamado encerrado."}
        </p>
      ) : (
        <div className="flex flex-col gap-2">
          {tickets.map((t) => (
            <button
              key={t.id}
              type="button"
              onClick={() => setSelectedId(t.id)}
              className="flex items-center justify-between gap-3 rounded-xl border border-gray-100 bg-gray-50 px-4 py-3 text-left transition-colors hover:bg-gray-100 dark:border-white/5 dark:bg-navy-900 dark:hover:bg-navy-700"
            >
              <div className="flex min-w-0 items-center gap-3">
                <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-white text-dv-blue-600 dark:bg-navy-800 dark:text-dv-cyan-300">
                  <MdConfirmationNumber className="h-4 w-4" aria-hidden="true" />
                </span>
                <div className="min-w-0">
                  <p className="truncate text-sm font-semibold text-navy-700 dark:text-white" title={t.subject}>
                    {t.subject}
                  </p>
                  <p className="truncate text-xs text-gray-600 dark:text-gray-400">
                    {t.number} · Aberto em {fmtDate(t.openedAt)}
                  </p>
                </div>
              </div>
              <span className={`shrink-0 rounded-full px-2.5 py-1 text-[11px] font-bold ${CLIENT_TICKET_STATE_TONE[t.state]}`}>
                {CLIENT_TICKET_STATE_LABELS[t.state]}
              </span>
            </button>
          ))}
        </div>
      )}

      {showOpenModal && (
        <OpenTicketModal
          onClose={() => setShowOpenModal(false)}
          onCreated={() => {
            setFilter("open");
            reload();
          }}
        />
      )}

      {selectedId !== null && (
        <TicketDetailModal
          ticketId={selectedId}
          onClose={() => setSelectedId(null)}
          onChanged={reload}
        />
      )}
    </div>
  );
};

const FilterBtn: React.FC<{ active: boolean; onClick: () => void; children: React.ReactNode }> = ({
  active,
  onClick,
  children,
}) => (
  <button
    type="button"
    onClick={onClick}
    className={`rounded-lg px-3.5 py-1.5 text-sm font-semibold transition-all ${
      active
        ? "bg-dv-blue-600 text-white shadow-dv-md"
        : "text-gray-600 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-navy-700"
    }`}
  >
    {children}
  </button>
);

export default TicketsTab;
